import React from 'react';
import { LibrarySection } from './LibrarySection';
import { LibraryMobileBulkActions } from '../library/components/LibraryMobileBulkActions';
import { WorkoutPlansSearchMobile } from '../library/workoutPlans/WorkoutPlansSearchMobile';
import { WorkoutPlansTableMobile } from '../library/workoutPlans/WorkoutPlansTableMobile';
import { ExercisesSearchMobile } from '../library/exercises/ExercisesSearchMobile';
import { ExercisesTableMobile } from '../library/exercises/ExercisesTableMobile';
import { WorkoutPlansToolbarDesktop } from '../library/workoutPlans/WorkoutPlansToolbarDesktop';
import { WorkoutPlansTableDesktop } from '../library/workoutPlans/WorkoutPlansTableDesktop';
import { ExercisesToolbarDesktop } from '../library/exercises/ExercisesToolbarDesktop';
import { ExercisesTableDesktop } from '../library/exercises/ExercisesTableDesktop';
import { WorkoutsTabDesktop, WorkoutsTabMobile } from '../library/workouts/WorkoutsTab';
import type { TrainerDashboardApi } from '../hooks/useTrainerDashboard';
import type { LibraryTab } from '../hooks/useLibraryLoader';

export type { TrainerDashboardApi };

export type TrainerDashboardLibraryPaneProps = {
  variant: 'mobile' | 'desktop';
  dash: TrainerDashboardApi;
};

function clearWorkoutSelections(dash: TrainerDashboardApi) {
  dash.setSelectedWorkouts(new Set());
  dash.setSelectedWorkoutPlans(new Set());
}

function onSelectTab(dash: TrainerDashboardApi, tab: LibraryTab) {
  dash.setActiveTab(tab);
  clearWorkoutSelections(dash);
}

function LibraryTabTables({ dash, variant }: TrainerDashboardLibraryPaneProps) {
  const d = dash;
  const profileId = d.profile?.id;

  if (variant === 'mobile') {
    return (
      <>
        <WorkoutsTabMobile
          activeTab={d.activeTab}
          wk={d.wk}
          workouts={d.workouts}
          profileId={profileId}
          selectedWorkouts={d.selectedWorkouts}
          openWorkoutMenu={d.openWorkoutMenu}
          setOpenWorkoutMenu={d.setOpenWorkoutMenu}
          openWorkoutFilterColumn={d.openWorkoutFilterColumn}
          setOpenWorkoutFilterColumn={d.setOpenWorkoutFilterColumn}
          workoutFilterRefs={d.workoutFilterRefs}
          workoutMenuRefs={d.workoutMenuRefs}
          trainerWorkoutDuplicateInFlight={d.trainerWorkoutDuplicateInFlight}
          onToggleWorkoutFilter={d.toggleWorkoutFilter}
          onSelectAllWorkouts={d.handleSelectAllWorkouts}
          onSelectWorkout={d.handleSelectWorkout}
          onWorkoutClick={d.handleWorkoutClick}
          onEditWorkout={d.handleEditWorkout}
          onDuplicateWorkout={d.handleDuplicateWorkout}
          onDeleteWorkout={d.handleDeleteWorkout}
        />
        {d.activeTab === 'workoutPlans' && (
          <WorkoutPlansTableMobile
            pl={d.pl}
            profileId={profileId}
            selectedWorkoutPlans={d.selectedWorkoutPlans}
            openWorkoutPlanMenu={d.openWorkoutPlanMenu}
            setOpenWorkoutPlanMenu={d.setOpenWorkoutPlanMenu}
            workoutPlanMenuRefs={d.workoutPlanMenuRefs}
            trainerPlanDuplicateInFlight={d.trainerPlanDuplicateInFlight}
            onSelectAllWorkoutPlans={d.handleSelectAllWorkoutPlans}
            onSelectWorkoutPlan={d.handleSelectWorkoutPlan}
            onEditWorkoutPlan={d.handleEditWorkoutPlan}
            onDuplicateWorkoutPlan={(id) => { void d.handleDuplicateWorkoutPlan(id); }}
            onAssignWorkoutPlan={d.handleAssignWorkoutPlan}
            onDeleteWorkoutPlan={d.handleDeleteWorkoutPlan}
          />
        )}
        {d.activeTab === 'exercises' && (
          <ExercisesTableMobile
            ex={d.ex}
            exerciseLoadError={d.exerciseLoadError}
            profileId={profileId}
            trainerDisplayName={d.trainerDisplayName}
            openExerciseMenu={d.openExerciseMenu}
            setOpenExerciseMenu={d.setOpenExerciseMenu}
            exerciseMenuRef={d.exerciseMenuRef}
            onViewExercise={d.handleViewExerciseListRow}
            onEditExercise={(id) => { void d.handleEditExerciseClickById(id); }}
            onDeleteExercise={d.handleDeleteExercise}
          />
        )}
      </>
    );
  }

  return (
    <>
      {d.activeTab === 'workoutPlans' && <WorkoutPlansToolbarDesktop pl={d.pl} activeTab={d.activeTab} />}
      {d.activeTab === 'exercises' && <ExercisesToolbarDesktop ex={d.ex} activeTab={d.activeTab} />}
      <div className="lib-legacy-tab">
        {d.loadingLibrary ? (
          <div className="lib-loading">
            <span className="lib-spinner" aria-hidden="true" />
            Loading library…
          </div>
        ) : (
          <>
            <WorkoutsTabDesktop
              activeTab={d.activeTab}
              wk={d.wk}
              workouts={d.workouts}
              profileId={profileId}
              selectedWorkouts={d.selectedWorkouts}
              openWorkoutMenu={d.openWorkoutMenu}
              setOpenWorkoutMenu={d.setOpenWorkoutMenu}
              workoutMenuRefs={d.workoutMenuRefs}
              trainerWorkoutDuplicateInFlight={d.trainerWorkoutDuplicateInFlight}
              libPageSelectionState={d.libPageSelectionState}
              onToggleSelectPage={d.handleLibToggleSelectPage}
              onSelectWorkout={d.handleSelectWorkout}
              onWorkoutClick={d.handleWorkoutClick}
              onEditWorkout={d.handleEditWorkout}
              onDuplicateWorkout={d.handleDuplicateWorkout}
              onDeleteWorkout={d.handleDeleteWorkout}
              onCreateWorkout={() => d.handleCreateNewWorkout()}
              onAssignWorkout={(id) => { void d.handleAssignWorkout(id); }}
            />
            {d.activeTab === 'workoutPlans' && (
              <WorkoutPlansTableDesktop
                pl={d.pl}
                workoutPlans={d.workoutPlans}
                profileId={profileId}
                selectedWorkoutPlans={d.selectedWorkoutPlans}
                openWorkoutPlanMenu={d.openWorkoutPlanMenu}
                setOpenWorkoutPlanMenu={d.setOpenWorkoutPlanMenu}
                workoutPlanMenuRefs={d.workoutPlanMenuRefs}
                trainerPlanDuplicateInFlight={d.trainerPlanDuplicateInFlight}
                libPlansPageSelectionState={d.libPlansPageSelectionState}
                onToggleSelectPlansPage={d.handleLibToggleSelectPlansPage}
                onSelectWorkoutPlan={d.handleSelectWorkoutPlan}
                onEditWorkoutPlan={d.handleEditWorkoutPlan}
                onDuplicateWorkoutPlan={(id) => { void d.handleDuplicateWorkoutPlan(id); }}
                onAssignWorkoutPlan={d.handleAssignWorkoutPlan}
                onDeleteWorkoutPlan={d.handleDeleteWorkoutPlan}
                onCreatePlan={() => d.navigate('/trainer-dashboard/workout-plan/new')}
              />
            )}
            {d.activeTab === 'exercises' && (
              <ExercisesTableDesktop
                ex={d.ex}
                exercises={d.exercises}
                profileId={profileId}
                trainerInitialsLabel={d.trainerInitialsLabel}
                selectedExercises={d.selectedExercises}
                openExerciseMenu={d.openExerciseMenu}
                setOpenExerciseMenu={d.setOpenExerciseMenu}
                exerciseMenuRef={d.exerciseMenuRef}
                libExercisesPageSelectionState={d.libExercisesPageSelectionState}
                onToggleSelectExercisesPage={d.handleLibToggleSelectExercisesPage}
                onToggleSelectExercise={d.handleLibToggleSelectExercise}
                onViewExercise={d.handleViewExerciseListRow}
                onEditExercise={(id) => { void d.handleLibraryEditExercise(id); }}
                onDuplicateExercise={(id) => { void d.handleDuplicateExerciseRow(id); }}
                onDeleteExercise={(id, name) => { void d.handleDeleteExercise(id, name); }}
                onBravosDeleteAttempt={() => {
                  d.setBulkDeleteToast("Bravos exercises can't be deleted from your library.");
                  window.setTimeout(() => d.setBulkDeleteToast(null), 3500);
                }}
                onCreateExercise={d.openCreateExerciseDrawer}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Library section — mobile + desktop shells with tab tables (R12). */
export function TrainerDashboardLibraryPane({ variant, dash }: TrainerDashboardLibraryPaneProps) {
  const d = dash;
  const isMobile = variant === 'mobile';

  const mobileSearch = isMobile ? (
    <>
      {d.activeTab === 'exercises' && <ExercisesSearchMobile ex={d.ex} />}
      {d.activeTab === 'workoutPlans' && <WorkoutPlansSearchMobile pl={d.pl} />}
    </>
  ) : undefined;

  return (
    <LibrarySection
      variant={variant}
      activeTab={d.activeTab}
      onSelectTab={(tab) => onSelectTab(d, tab)}
      libTabCount={d.libTabCount}
      loadingLibrary={d.loadingLibrary}
      primaryCta={d.libPrimaryCta}
      mobileCreateMenu={
        isMobile
          ? {
              isOpen: d.isCreateMenuOpen,
              onToggle: () => d.setIsCreateMenuOpen(!d.isCreateMenuOpen),
              menuRef: d.createMenuRef,
              onCreateExercise: () => {
                d.setIsCreateMenuOpen(false);
                d.openCreateExerciseDrawer();
              },
              onCreateWorkout: () => {
                d.setIsCreateMenuOpen(false);
                d.handleCreateNewWorkout();
              },
              onCreateWorkoutPlan: () => {
                d.setIsCreateMenuOpen(false);
                d.navigate('/trainer-dashboard/workout-plan/new');
              },
            }
          : undefined
      }
      mobileBulkActions={
        isMobile ? (
          <LibraryMobileBulkActions
            activeTab={d.activeTab}
            selectedWorkoutsCount={d.selectedWorkouts.size}
            selectedWorkoutPlansCount={d.selectedWorkoutPlans.size}
            isActionsMenuOpen={d.isActionsMenuOpen}
            isWorkoutPlanActionsMenuOpen={d.isWorkoutPlanActionsMenuOpen}
            actionsMenuRef={d.actionsMenuRef as React.RefObject<HTMLDivElement>}
            workoutPlanActionsMenuRef={d.workoutPlanActionsMenuRef as React.RefObject<HTMLDivElement>}
            trainerWorkoutDuplicateInFlight={d.trainerWorkoutDuplicateInFlight}
            trainerPlanDuplicateInFlight={d.trainerPlanDuplicateInFlight}
            onToggleActionsMenu={() => d.setIsActionsMenuOpen(!d.isActionsMenuOpen)}
            onTogglePlanActionsMenu={() => d.setIsWorkoutPlanActionsMenuOpen(!d.isWorkoutPlanActionsMenuOpen)}
            onCloseActionsMenu={() => d.setIsActionsMenuOpen(false)}
            onClosePlanActionsMenu={() => d.setIsWorkoutPlanActionsMenuOpen(false)}
            onDuplicateWorkouts={() => { void d.handleDuplicateSelectedWorkouts(); }}
            onDeleteWorkouts={d.handleDeleteSelectedWorkouts}
            onDuplicatePlans={() => { void d.handleDuplicateSelectedWorkoutPlans(); }}
            onDeletePlans={d.handleDeleteSelectedWorkoutPlans}
          />
        ) : undefined
      }
      mobileSearchSlot={mobileSearch}
      desktopSelection={
        !isMobile
          ? {
              selectedWorkoutsCount: d.selectedWorkouts.size,
              selectedWorkoutPlansCount: d.selectedWorkoutPlans.size,
              selectedExercisesCount: d.selectedExercises.size,
              onDuplicateWorkouts: () => { void d.handleDuplicateSelectedWorkouts(); },
              onDeleteWorkouts: () => { void d.handleDeleteSelectedWorkouts(); },
              onCloseWorkouts: () => d.setSelectedWorkouts(new Set()),
              workoutDuplicateDisabled: d.trainerWorkoutDuplicateInFlight,
              onDuplicatePlans: () => { void d.handleDuplicateSelectedWorkoutPlans(); },
              onDeletePlans: () => { void d.handleDeleteSelectedWorkoutPlans(); },
              onClosePlans: () => d.setSelectedWorkoutPlans(new Set()),
              planDuplicateDisabled: d.trainerPlanDuplicateInFlight,
              onDuplicateExercises: () => { void d.handleBulkDuplicateExercises(); },
              onDeleteExercises: d.handleRequestBulkDeleteExercises,
              onCloseExercises: () => d.setSelectedExercises(new Set()),
              exerciseDuplicateDisabled: d.bulkDuplicateExercisesInFlight,
            }
          : undefined
      }
    >
      <LibraryTabTables dash={dash} variant={variant} />
    </LibrarySection>
  );
}
